import { type AccountView, type AuthenticatedSession } from './api';
import { type StoredSession } from './session-store';

/**
 * The token's whole life, as a value.
 *
 * Every screen the account half of this client can show is one of four states,
 * and every way of moving between them is a function here. None of it touches a
 * browser, a socket or React — which is the point: *what a visitor is shown* is
 * the part with an answer in it, and a decision that only exists inside a
 * `useEffect` is a decision nothing can test (the same argument `world.ts`
 * makes for the renderer's side).
 *
 * ## Why a stored token is `restoring` and not `signed-in`
 *
 * A token in storage is a claim, not a proof. A client that trusted it would
 * draw the character list, ask for the roster, and receive a 401 — so the
 * *first* thing a returning player would see is an empty roster, and only then a
 * sign-in screen. Checking the token before drawing anything means a returning
 * player sees either their characters or an honest "you have been signed out",
 * and never a screen built on a session that had already ended.
 *
 * ## Why signing out and being signed out are different states
 *
 * `signed-out` and `session-ended` render the same form and mean opposite
 * things. A player who clicked *sign out* knows why they are looking at it; a
 * player who came back the next morning does not, and showing them a blank form
 * is the same failure as showing a stranger "unauthorised" over a blank canvas.
 * Keeping the reason in the state is what lets the screen say which happened.
 */

/** Why a visitor is looking at the sign-in screen. Three answers, three sentences. */
export type SignedOutReason =
  /** Nothing was stored. A first visit, or a new tab. */
  | 'never-signed-in'
  /** They asked to leave. */
  | 'signed-out'
  /** A token they were holding is no longer accepted — expired, or refused for a reason nobody is told. */
  | 'session-ended';

export type AccountState =
  /** A token was found in storage and is being checked. Nothing is drawn from it yet. */
  | { readonly status: 'restoring'; readonly token: string; readonly characterId: string | null }
  | { readonly status: 'signed-out'; readonly reason: SignedOutReason }
  | {
      readonly status: 'signed-in';
      readonly token: string;
      readonly account: AccountView;
      /** The character being played, or `null` while the roster is on screen. */
      readonly characterId: string | null;
    };

/** Where a page load starts, given whatever the tab had stored. */
export const startFrom = (stored: StoredSession | null): AccountState =>
  stored === null
    ? { status: 'signed-out', reason: 'never-signed-in' }
    : { status: 'restoring', token: stored.token, characterId: stored.characterId };

/** The stored token was checked and the API named an account. */
export const restored = (state: AccountState, account: AccountView): AccountState =>
  state.status === 'restoring'
    ? { status: 'signed-in', token: state.token, account, characterId: state.characterId }
    : state;

/** A register or a login answered. `characterId` so a caller can land somebody straight back in a world. */
export const signedIn = (session: AuthenticatedSession, characterId: string | null): AccountState => ({
  status: 'signed-in',
  token: session.token,
  account: session.account,
  characterId,
});

export const chose = (state: AccountState, characterId: string): AccountState =>
  state.status === 'signed-in' ? { ...state, characterId } : state;

/** Back to the roster, still signed in. */
export const leftCharacter = (state: AccountState): AccountState =>
  state.status === 'signed-in' ? { ...state, characterId: null } : state;

export const signedOut = (): AccountState => ({ status: 'signed-out', reason: 'signed-out' });

/**
 * A token that was being held is gone.
 *
 * The one transition every authenticated call must be able to make, from
 * anywhere. Without it, a returning player whose token expired overnight is
 * stranded on whatever screen made the request — which for the world is a dead
 * canvas, forever.
 */
export const sessionEnded = (): AccountState => ({ status: 'signed-out', reason: 'session-ended' });

/** The token to put on a request, or `null` when there is none to put. */
export const tokenOf = (state: AccountState): string | null =>
  state.status === 'signed-out' ? null : state.token;

/**
 * What to persist, or `null` when nothing should be.
 *
 * `restoring` writes nothing on purpose: it would be writing back exactly what
 * was just read, and it is the one window in which a token the API is in the
 * middle of rejecting could be re-persisted by a race between the check and the
 * write.
 */
export const storableFrom = (state: AccountState): StoredSession | null =>
  state.status === 'signed-in' ? { token: state.token, characterId: state.characterId } : null;
